import { DatePipe } from "@angular/common";
import {
    AfterContentInit,
    Component,
    inject,
    Input,
    OnDestroy,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { FileUploadComponent , FileBase64 } from "@components/file-upload/file-upload.component";
import { TranslateDirective, TranslatePipe, TranslateService } from "@ngx-translate/core";
import { firstValueFrom, Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";

import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller } from "~app/common/models/constants";
import { BilagaDTO } from "~app/common/models/virkeskop/v2/BilagaDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { Utils } from "~app/common/services/utils";
import { KontraktBilagorService } from "~app/features/virkeskop/services/kontrakt-bilagor.service";
import { canEdit, updatePermissions } from "~app/role-permissions";
import { DigitalSigneringMediatorService } from "../../services/digitalsignering-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";

@Component({
    selector: "rask-kontrakt-bilagor",
    styleUrls: ["./kontrakt-bilagor.component.scss"],
    templateUrl: "./kontrakt-bilagor.component.html",
    imports: [
        TranslateDirective,
        FileUploadComponent,
        CheckboxComponent,
        FormsModule,
        ButtonComponent,
        TranslatePipe,
        DatePipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        updatePermissions({
            role: RaskRoller.role.Kopplanera,
            canEdit: false
        })
    ]
})
export class KontraktBilagorComponent implements AfterContentInit, OnDestroy {
    acceptedFiletypes = [
        "xls",
        "xlsx",
        "doc",
        "docx",
        "pdf",
        "txt",
        "msg",
        "jpg",
        "jpeg",
        "png",
        "zip"
    ];
    bilagor: BilagaDTO[] = [];
    fileActionInProgress = false;
    fileName = "";
    fileUploadError = "";
    @Input() kontraktId: number;
    maxFileNameLength = 100;
    maxFileSize = 1024 * 1024 * 50;
    @Input() readonly = false;

    private files: FileBase64[] = [];

    private readonly authenticationService = inject(AuthenticationService);
    private readonly destroySrc = new Subject<void>();
    private readonly digitalSigneringMediatorService = inject(DigitalSigneringMediatorService);
    private readonly kontraktBilagorService = inject(KontraktBilagorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly modalDialogService = inject(RaskModalService);
    private readonly translate = inject(TranslateService);
    private readonly canEdit = canEdit();

    get hasEditFilePermission() {
        return this.authenticationService.hasRoles([
            RaskRoller.role.Kopleda,
            RaskRoller.role.Virkeskopa,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }

    get hasUploadFilePermission() {
        return this.authenticationService.hasRoles([
            RaskRoller.role.Kopleda,
            RaskRoller.role.Virkeskopa,
            RaskRoller.role.Kopplanera,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }

    get hasViewFilePermission() {
        return this.hasUploadFilePermission;
    }

    get uploadButtonInactive() {
        return this.fileActionInProgress || this.fileName.length <= 0;
    }

    get isSkogsvard(): boolean {
        return this.kontraktMediatorService.kontrakt.isSkogsvardFPVKontrakt;
    }
    deleteBilaga(bilaga: BilagaDTO) {
        if (this.fileActionInProgress || !this.hasEditFilePermission) {
            return;
        }
        this.modalDialogService.confirm(new ModalSettings(this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_TA_BORT_BILAGA"), bilaga.fileName))
            .subscribe(modalResult => {
                if (modalResult === ConfirmDialogResponse.save && bilaga.fileId > 0 && this.bilagor.findIndex(b => b.fileId === bilaga.fileId) > -1 && !this.fileActionInProgress) {
                    this.fileActionInProgress = true;
                    this.kontraktBilagorService.deleteBilaga(bilaga.fileId)
                        .then(response => {
                            this.bilagor.splice(this.bilagor.findIndex(b => b.fileId === bilaga.fileId), 1);
                        })
                        .catch(err => {
                            this.fileUploadError = err;
                        })
                        .then(() => {
                            this.fileActionInProgress = false;
                        });
                }
            });
    }

    ngAfterContentInit(): void {
        this.reloadBilagor();

        this.kontraktBilagorService.reloadBilagorSrc.pipe(
            tap(() => this.reloadBilagor()),
            takeUntil(this.destroySrc)
        ).subscribe();
    }

    ngOnDestroy(): void {
        this.destroySrc.next();
        this.destroySrc.complete();
    }

    async onFileSelected(files: FileBase64[]) {
        this.fileUploadError = "";

        files.forEach(f => {
            const fileExtension = f.name.split(".").pop();
            if (this.acceptedFiletypes.indexOf(fileExtension.toLowerCase()) === -1) {
                this.fileUploadError = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_TILLATNA_FILTYPER");
            } else if (f.size > this.maxFileSize) {
                this.fileUploadError = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_MAX_UPLOAD_STORLEK");
            } else if (f.name.length > this.maxFileNameLength) {
                this.fileUploadError = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_MAX_FILNAMN_STORLEK");
            }
        });

        if (this.fileUploadError?.length > 0) {
            this.clearFileInput();
        } else {
            this.files = files;
            this.fileName = files.map(f => f.name).join(", ");
        }
    }

    reloadBilagor() {
        this.bilagor.splice(0);
        if (this.kontraktId) {
            this.kontraktBilagorService.getBilagor(this.kontraktId)
                .subscribe(response => {
                    if (response.bilagor.length > 0) {
                        response.bilagor.forEach(b => {
                            const convertedDate = new Date(b.fileDate);
                            this.bilagor.push({
                                fileDate: `${convertedDate.toLocaleDateString("sv-SE")} ${convertedDate.toLocaleTimeString("sv-SE")}`,
                                fileId: b.fileId,
                                fileName: b.fileName,
                                fileVersion: b.fileVersion,
                                synligForKopplanera: b.synligForKopplanera
                            });
                        });
                    }
                });
        }
    }

    synligForKopplaneraChange(bilaga: BilagaDTO, synlighet: boolean) {
        this.kontraktBilagorService.updateBilaga(bilaga.fileId, synlighet);
    }

    openDokument(bilaga: BilagaDTO) {
        this.kontraktBilagorService.getStreamForBilaga(this.kontraktId, bilaga.fileId).subscribe((blob: Blob) => {
            const url = URL.createObjectURL(blob);
            window.open(url);
        });
        return false;
    }

    downloadDokument(bilaga: BilagaDTO) {
        this.kontraktBilagorService.getStreamForBilaga(this.kontraktId, bilaga.fileId).subscribe((blob: Blob) => {
            Utils.downloadBlob(blob, bilaga.fileName);
        });
        return false;
    }

    async uploadFile() {
        const confirmed = await this.confirmAktivDigitalSignering();
        if (confirmed === ConfirmDialogResponse.cancel) {
            return;
        }

        this.fileActionInProgress = true;
        if (this.files.length === 0) {
            return;
        }

        this.kontraktBilagorService.uploadBilaga({
            kontraktId: this.kontraktId,
            files: this.files
        }).then(response => {
            response.bilagor.forEach(bilaga => {
                const index = this.bilagor.findIndex(b => b.fileName === bilaga.fileName);
                if (index > -1) {
                    {
                        this.bilagor = [
                            ...this.bilagor.slice(0, index),
                            bilaga,
                            ...this.bilagor.slice(index + 1)
                        ];
                    }
                } else {
                    this.bilagor = [
                        ...this.bilagor,
                        bilaga
                    ];
                }
            });
        }).catch(err => {
            this.fileUploadError = err;

        }).finally(() => {
            this.clearFileInput();
            this.fileActionInProgress = false;
        });
    }

    private readonly clearFileInput = () => {
        this.files = [];
        this.fileName = "";
    };

    private async confirmAktivDigitalSignering() {
        const hasAktivSignering = await firstValueFrom(this.digitalSigneringMediatorService.hasAktivSignering$);

        if (!hasAktivSignering) {
            return true;
        }
        const modalResult$ = this.modalDialogService.confirm(new ModalSettings("Varning",
            `Det finns aktiva digitala signeringar. Är du säker på att du vill ladda upp '${this.fileName}'?`));
        return firstValueFrom(modalResult$);
    }
}
